/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path app
 * @description Application views starting point
 *
 * @section 4.1.2
 *
 */
import React from "react";
import { useLocation, useParams, useHistory } from "react-router-dom";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useLanguageContext from "./../modules/useLanguageContext";
import { getYearPeriod } from "./../context/periodDefaults";
import { getModelIdForYear } from "./../context/modelsByYear";

/**
 * Extracts a 4-digit year from a period string.
 */
const extractYearFromPeriod = (periodName: string): string | null => {
  if (!periodName) return null;
  
  // Pattern 1: "X / YYYY" or "QN / YYYY" format
  const slashMatch = periodName.match(/\s*\/\s*(20\d{2})\b/);
  if (slashMatch) return slashMatch[1];
  
  // Pattern 2: Just a 4-digit year at the end
  const yearAtEnd = periodName.match(/(20\d{2})\b/);
  if (yearAtEnd) return yearAtEnd[1];
  
  // Pattern 3: Try getYearPeriod helper (for quarter-based periods)
  try {
    const periodYear = getYearPeriod(periodName);
    const yearMatch = periodYear.match(/(20\d{2})$/);
    if (yearMatch) return yearMatch[1];
  } catch (e) {
    // Fallback if getYearPeriod fails
  }
  
  return null;
};

/**
 * Gets the model ID based on the current period.
 */
const getModelIdFromPeriod = (period: string | undefined): string | null => {
  if (!period) return null;
  
  const year = extractYearFromPeriod(period);
  if (year) {
    return getModelIdForYear(year);
  }

  return null;
};

const View = (props: React.PropsWithChildren<{}>) => {
  const params = useParams<{
    id: string;
    meaId: string;
    period: string;
    model: string;
    module: string;
  }>();
  useLanguageContext();
  const sendValue = useAppContext((s) => s.sendValue);
  const ctx = useAppContext();
  const location = useLocation();
  const history = useHistory();
  const [, tenantSymbol, rootPathElement] = location.pathname.split("/");
  const periodParam = new URLSearchParams(location.search).get("period");
  const decodedPeriod = periodParam
    ? decodeURIComponent(periodParam).trim().replace(/\s+/g, " ")
    : "";

  React.useEffect(() => {
    sendValue("rootUrl", ["", tenantSymbol, rootPathElement].join("/"));
  }, [tenantSymbol, rootPathElement, sendValue]);
  React.useEffect(() => {
    // Handle element ID when model changes
    // If element ID is from a different model, we need to either:
    // 1. Clear it and let the page navigate to the root, OR
    // 2. Find the corresponding element in the new model (complex, requires symbol matching)
    // For now, we'll clear it if models don't match, which will cause navigation to root element
    if (params.meaId) {
      const scMatch = params.meaId.match(/^SC\.(\d{10})\./);
      if (scMatch) {
        const elementModelId = scMatch[1];
        // Only set elementId if the element's model matches the URL model
        // If models don't match, clear elementId to prevent data fetching with wrong model
        if (elementModelId === params.model || elementModelId === ctx.modelId) {
          sendValue("elementId", params.meaId);
        } else {
          // Element ID is from different model - clear it
          // This will cause the page to navigate to root or handle the mismatch
          console.log(`[Views] Element ID ${params.meaId} has model ${elementModelId} but URL has model ${params.model}, clearing elementId`);
          sendValue("elementId", "");
        }
      } else {
        // Element ID doesn't have model prefix, safe to set
        sendValue("elementId", params.meaId);
      }
    } else {
      sendValue("elementId", "");
    }
  }, [params.meaId, params.model, ctx.modelId, sendValue]);
  // Track if we're updating to prevent multiple simultaneous updates
  const isUpdatingRef = React.useRef(false);
  
  React.useEffect(() => {
    // Skip if already updating to prevent multiple simultaneous updates
    if (isUpdatingRef.current) {
      return;
    }
    
    // If URL has period, use it (it takes priority)
    // Otherwise, try to load from session storage (check expiration first)
    const periodToUse = decodedPeriod || (() => {
      try {
        const timestamp = sessionStorage.getItem("tdf_selected_timestamp");
        if (!timestamp) return null;
        
        const storedTime = parseInt(timestamp, 10);
        const currentTime = Date.now();
        if (currentTime - storedTime > 20 * 60 * 1000) {
          // Session expired - clear all stored values
          sessionStorage.removeItem("tdf_selected_period");
          sessionStorage.removeItem("tdf_selected_model_id");
          sessionStorage.removeItem("tdf_selected_timestamp");
          return null;
        }
        return sessionStorage.getItem("tdf_selected_period");
      } catch (e) {
        return null;
      }
    })();
    
    if (periodToUse) {
      sendValue("period", periodToUse);
      
      // Update model ID based on period year if needed
      // If URL has model, use it; otherwise try session storage (check expiration first), then derive from period
      const urlModelId = params.model || (() => {
        try {
          const timestamp = sessionStorage.getItem("tdf_selected_timestamp");
          if (!timestamp) return null;
          
          const storedTime = parseInt(timestamp, 10);
          const currentTime = Date.now();
          if (currentTime - storedTime > 20 * 60 * 1000) {
            // Session expired - clear all stored values
            sessionStorage.removeItem("tdf_selected_period");
            sessionStorage.removeItem("tdf_selected_model_id");
            sessionStorage.removeItem("tdf_selected_timestamp");
            return null;
          }
          return sessionStorage.getItem("tdf_selected_model_id");
        } catch (e) {
          return null;
        }
      })();
      
      const targetModelId = urlModelId || getModelIdFromPeriod(periodToUse);
      if (targetModelId && targetModelId !== ctx.modelId && targetModelId !== params.model) {
        isUpdatingRef.current = true;
        
        // Update context
        sendValue("modelId", targetModelId);
        
        // Update URL to match the correct model (only if model actually changed)
        const currentPath = location.pathname;
        const pathParts = currentPath.split("/").filter(Boolean);
        // Path format: /tenant/model/page/...
        // Make sure we have valid path structure before updating
        if (pathParts.length >= 3 && pathParts[0] === tenantSymbol) {
          // Check if the second part is a valid model ID (numeric) and third part is a page name
          const currentModelInPath = pathParts[1];
          const pageParts = pathParts.slice(2);
          
          // Only update if:
          // 1. Current model in path doesn't match target model
          // 2. We have a valid page name (not another model ID)
          // 3. The page name doesn't look like a model ID (all numeric and 10 digits)
          const isValidPage = pageParts.length > 0 && 
            !(pageParts[0].match(/^\d{10}$/) && pageParts[0] !== targetModelId);
          
          if (currentModelInPath !== targetModelId && isValidPage) {
            const page = pageParts.join("/");
            const newPath = `/${tenantSymbol}/${targetModelId}/${page}${location.search}`;
            if (newPath !== currentPath + location.search) {
              history.replace(newPath);
            }
          }
        }
        
        // Reset update flag after a delay
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 200);
      }
    }
  }, [
    decodedPeriod,
    sendValue,
    ctx.modelId,
    params.model,
    location.pathname,
    location.search,
    tenantSymbol,
    history,
  ]);
  React.useEffect(() => {
    // Only set page if params.id exists and is not a model ID (not all numeric 10 digits)
    // This prevents model IDs from being treated as page names
    if (params.id && !params.id.match(/^\d{10}$/)) {
      sendValue("page", params.id);
    } else if (params.id && params.id.match(/^\d{10}$/)) {
      // If params.id is a model ID, try to extract page from pathname
      const pathParts = location.pathname.split("/").filter(Boolean);
      if (pathParts.length >= 3 && pathParts[0] === tenantSymbol) {
        // Path format: /tenant/model/page/...
        // If second part is model and third part is also model, skip
        // Otherwise, use the third part as page
        const potentialPage = pathParts[2];
        if (potentialPage && !potentialPage.match(/^\d{10}$/)) {
          sendValue("page", potentialPage);
        }
      }
    }
  }, [params.id, sendValue, location.pathname, tenantSymbol]);
  React.useEffect(() => {
    if (ctx.rtl) {
      document.body.classList.add("arabic");
    } else {
      document.body.classList.remove("arabic");
    }
  }, [ctx.rtl]);
  return <>{props.children}</>;
};
export default View;
